<template>
  <div class="gs-aff-panel">
    <header class="gs-aff-panel__head">
      <div>
        <h3>Commission overrides</h3>
        <p>Optional exceptions for price points that should pay differently from the default, or only to specific affiliates.</p>
      </div>
    </header>

    <div v-if="localModel.pricing_points.length > 6" class="gs-aff-panel__meta">
      <label class="gs-aff-search">
        <gs-icon name="search" size="16"></gs-icon>
        <input v-model="search" type="search" placeholder="Search price points" aria-label="Search price points" />
      </label>
      <span class="gs-aff-count">{{ overrideTotal }} override{{ overrideTotal === 1 ? '' : 's' }} across {{ localModel.pricing_points.length }} price points</span>
    </div>

    <div class="gs-aff-list">
      <article
        v-for="{ pricePoint, index } in visiblePricePoints"
        :key="pricePoint.id || index"
        class="gs-aff-row"
        :class="{
          'is-open': openIndex === index,
          'is-configured': overrideCommissions(pricePoint).length > 0,
          'has-error': hasError(index),
        }"
      >
        <button
          type="button"
          class="gs-aff-row__head"
          :aria-expanded="openIndex === index ? 'true' : 'false'"
          @click="toggle(index)"
        >
          <span class="gs-aff-row__dot" aria-hidden="true"></span>
          <span class="gs-aff-row__title">
            <span class="gs-aff-row__name">{{ pricePoint.name }}</span>
            <span class="gs-aff-row__summary">{{ rowSummary(pricePoint) }}</span>
          </span>
          <span class="gs-aff-row__meta">
            <span v-if="hasError(index)" class="gs-aff-pill gs-aff-pill--danger">Needs attention</span>
            <span v-else-if="overrideCommissions(pricePoint).length" class="gs-aff-pill gs-aff-pill--success">
              {{ overrideCommissions(pricePoint).length }} override{{ overrideCommissions(pricePoint).length === 1 ? '' : 's' }}
            </span>
            <span v-else class="gs-aff-pill">Uses default</span>
          </span>
          <span class="gs-aff-row__chevron" aria-hidden="true">
            <gs-icon :name="openIndex === index ? 'chevron-up' : 'chevron-down'" size="18"></gs-icon>
          </span>
        </button>

        <div v-if="openIndex === index" class="gs-aff-row__body">
          <div v-if="overrideCommissions(pricePoint).length === 0" class="gs-aff-empty">
            <strong>No override on this price point</strong>
            <p>It pays the default commission. Add an override to pay a different amount or limit it to specific affiliates.</p>
            <gs-button type="button" variant="primary" size="sm" @click="createCommissionOverride(index)">
              <gs-icon name="plus" size="14"></gs-icon>
              Add override
            </gs-button>
          </div>

          <template v-else>
            <div
              v-for="commissionTier in overrideCommissions(pricePoint)"
              :key="commissionTier.id"
              class="gs-aff-override gs-aff-section"
            >
              <div class="gs-aff-grid">
                <label class="gs-aff-field">
                  <span class="gs-aff-field__label">Override name</span>
                  <input v-model="commissionTier.name" class="gs-aff-input" type="text" placeholder="e.g. Launch partner override" />
                  <span v-if="!commissionTier.name" class="gs-aff-field__hint">A name is required to save.</span>
                </label>
                <div class="gs-aff-field">
                  <span class="gs-aff-field__label">Available to</span>
                  <gs-button
                    type="button"
                    variant="outline"
                    class="gs-aff-override__availability"
                    @click="openCommissionOverridesAffModal(index, realCommissionIndex(pricePoint, commissionTier))"
                  >
                    <gs-icon name="users" size="16"></gs-icon>
                    {{ availabilityLabel(commissionTier) }}
                  </gs-button>
                </div>
              </div>

              <section class="gs-aff-section">
                <div class="gs-aff-section__head">
                  <div>
                    <strong>{{ pricePoint.is_trial == 1 ? 'Initial charge or trial conversion' : 'First payment' }}</strong>
                    <p>What affiliates earn when the buyer makes the first billable payment.</p>
                  </div>
                </div>
                <div class="gs-aff-grid">
                  <label class="gs-aff-field">
                    <span class="gs-aff-field__label">Tier 1 affiliate</span>
                    <span class="gs-aff-input-pair">
                      <input v-model="primaryCommission(pricePoint, commissionTier).commission" class="gs-aff-input" type="number" min="0" aria-label="Tier 1 commission" />
                      <select v-model="primaryCommission(pricePoint, commissionTier).commission_type" class="gs-aff-select" aria-label="Tier 1 commission type">
                        <option v-for="option in commissionTypeOptions" :key="option.value" :value="option.value">{{ option.text }}</option>
                      </select>
                    </span>
                  </label>
                  <label class="gs-aff-field">
                    <span class="gs-aff-field__label">Tier 2 (optional)</span>
                    <span class="gs-aff-input-pair">
                      <input v-model="primaryCommission(pricePoint, commissionTier).jv_broker_commission" class="gs-aff-input" type="number" min="0" aria-label="Tier 2 commission" />
                      <select v-model="primaryCommission(pricePoint, commissionTier).jv_broker_commission_type" class="gs-aff-select" aria-label="Tier 2 commission type">
                        <option v-for="option in commissionTypeOptions" :key="option.value" :value="option.value">{{ option.text }}</option>
                      </select>
                    </span>
                  </label>
                </div>
              </section>

              <recurring-schedule
                v-if="pricePoint.type != 1"
                :commission-tier="commissionTier"
                :commission-type-options="commissionTypeOptions"
              />

              <div class="gs-aff-actions">
                <button
                  type="button"
                  class="gs-aff-link-button"
                  @click="openConfirmModal(index, realCommissionIndex(pricePoint, commissionTier))"
                >
                  Delete this override
                </button>
              </div>
            </div>

            <div class="gs-aff-row__footer">
              <span class="gs-aff-count">Overrides replace the default commission for the affiliates they apply to.</span>
              <gs-button type="button" variant="outline" size="sm" @click="createCommissionOverride(index)">
                <gs-icon name="plus" size="14"></gs-icon>
                Add another override
              </gs-button>
            </div>
          </template>
        </div>
      </article>

      <div v-if="!visiblePricePoints.length" class="gs-aff-empty">
        <strong>No matching price points</strong>
        <p>Try a different search.</p>
      </div>
    </div>

    <gs-alert-dialog
      v-model="showDeleteCommissionConfirm"
      title="Delete commission override?"
      :copy="$t('frontend_non_revertable')"
      confirm-label="Delete"
      cancel-label="Cancel"
      @confirm="deleteCommissionTier"
    />
  </div>
</template>

<script>
import RecurringSchedule from './RecurringSchedule';
import { commissionSummary, primaryCommission, commissionTypeOptions } from './commissionFormat';

export default {
  components: {
    RecurringSchedule,
  },
  props: {
    model: {
      type: Object,
      required: true
    },
    validation: {
      required: true
    },
    openCommissionOverridesAffModal: {
      type: Function,
      required: true
    },
    focusPricePointIndex: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      localModel: this.model,
      pricePointIndex: null,
      commissionTierIndex: null,
      showDeleteCommissionConfirm: false,
      openIndex: this.focusPricePointIndex,
      search: '',
    };
  },
  computed: {
    commissionTypeOptions() {
      return commissionTypeOptions(this.$t.bind(this));
    },
    visiblePricePoints() {
      const term = this.search.trim().toLowerCase();
      return this.localModel.pricing_points
        .map((pricePoint, index) => ({ pricePoint, index }))
        .filter(({ pricePoint }) => !term || String(pricePoint.name || '').toLowerCase().includes(term));
    },
    overrideTotal() {
      return this.localModel.pricing_points.reduce((total, pricePoint) => total + this.overrideCommissions(pricePoint).length, 0);
    },
  },
  watch: {
    model: {
      handler() {
        this.$emit('update-model', this.localModel);
      },
      deep: true
    }
  },
  methods: {
    primaryCommission,
    toggle(index) {
      this.openIndex = this.openIndex === index ? null : index;
    },
    hasError(index) {
      const pricePoints = this.validation && this.validation.localModel && this.validation.localModel.pricing_points;
      const entry = pricePoints && pricePoints.$each && pricePoints.$each[index];
      return Boolean(entry && entry.$error);
    },
    overrideCommissions(pricePoint) {
      return (pricePoint.commission_details || []).filter((obj) => obj.type != 1);
    },
    realCommissionIndex(pricePoint, commissionTier) {
      return (pricePoint.commission_details || []).indexOf(commissionTier);
    },
    rowSummary(pricePoint) {
      const overrides = this.overrideCommissions(pricePoint);
      if (!overrides.length) {
        return 'Pays the default commission';
      }
      if (overrides.length > 1) {
        return overrides.map((tier) => tier.name || 'Unnamed override').join(' · ');
      }
      return `${overrides[0].name || 'Unnamed override'} · ${commissionSummary(pricePoint, overrides[0])}`;
    },
    availabilityLabel(commissionTier) {
      if (!commissionTier.assigned_to || !commissionTier.assigned_to.length) {
        return 'All affiliates';
      }

      return `${commissionTier.assigned_to.length} selected affiliate${commissionTier.assigned_to.length === 1 ? '' : 's'}`;
    },
    createCommissionOverride(index) {
      const commissionId = Math.ceil(Math.random() * 1000);
      const length = this.localModel.pricing_points[index].commission_details.length;
      this.localModel.pricing_points[index].commission_details.push({
        id: commissionId,
        type: 2,
        name: `Commission override #${length}`,
        assigned_to: null,
        sales_commission: {
          commission: 0,
          commission_type: 'percent',
          jv_broker_commission: 0,
          jv_broker_commission_type: 'percent',
        },
        trial_commission: {
          commission: 0,
          commission_type: 'percent',
          jv_broker_commission: 0,
          jv_broker_commission_type: 'percent',
        },
        rebill_commissions: [
          {
            commission: 50,
            no_of_payments: null,
            commission_type: 'percent',
            jv_broker_commission: 0,
            jv_broker_commission_type: 'percent',
          },
        ],
      });
    },
    openConfirmModal(index, commissionTierIndex) {
      this.pricePointIndex = index;
      this.commissionTierIndex = commissionTierIndex;
      this.showDeleteCommissionConfirm = true;
    },
    async deleteCommissionTier() {
      this.localModel.pricing_points[this.pricePointIndex].commission_details.splice(this.commissionTierIndex, 1);
    },
  },
};
</script>
